import { useState, useMemo, useEffect } from "react";
import { Card, TerminologyOptions } from "@/types/v2";
import { useStore } from "@/stores/useStore";
import { useLayouts } from "@/hooks/useLayouts";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";
import { httpsCallables } from "@/repositories/httpsCallables";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Base/Dialog";
import { Autocomplete } from "@/components/Base/Autocomplete";
import { Button } from "@/components/Base/Button";
import { Icon } from "@/components/Common/Icon";
import { IconNames } from "@/types/v2";
import { LayoutSelector } from "@/components/Dashboard/LayoutSelector";
import { useLabelChoiceDialog } from "@/components/Common/LabelChoiceDialog";
import { resolveBidirectionalLabelChoice } from "@/lib/resolveBidirectionalLabelChoice";
import { useTechnicianChoiceDialog } from "@/components/Common/TechnicianChoiceDialog";
import { resolveTechnicianChoice } from "@/lib/resolveTechnicianChoice";
import { isTechnicianWritebackEnabledForShop } from "@/lib/technicianWriteback";
import { useRoles } from "@/hooks/useRoles";
import { toast } from "@/hooks/useToast";

export interface MoveToDialogProps {
  isOpen: boolean;
  onClose: () => void;
  card: Card;
  onMoveSuccess?: (card: Card, newContainer: string) => void;
}

interface MoveToDialogState {
  selectedLayoutName: string | undefined;
  selectedContainerId: string | undefined;
  isLoading: boolean;
  error: string | undefined;
}

export const MoveToDialog = ({
  isOpen,
  onClose,
  card,
  onMoveSuccess,
}: MoveToDialogProps) => {
  const { shop, dashboardLayoutId, employees } = useStore();
  const { layouts } = useLayouts();
  const { terminolize } = useTerminologyReplacer();

  const [state, setState] = useState<MoveToDialogState>({
    selectedLayoutName: undefined,
    selectedContainerId: undefined,
    isLoading: false,
    error: undefined,
  });

  const { chooseLabel, Dialog: LabelChoicePopup } = useLabelChoiceDialog();
  const { chooseTechnician, Dialog: TechnicianChoicePopup } =
    useTechnicianChoiceDialog();
  const { isAdmin, isOffice, isSuperAdmin } = useRoles();
  const isTechnicianWritebackEnabled = shop
    ? isTechnicianWritebackEnabledForShop(shop)
    : false;
  // WEBV3-293: see the matching comment in Dashboard.tsx — same UX gate,
  // same backend enforcement (moveCard.ts's canAssignTechnician clamp).
  const canAssignTechnician = isAdmin || isOffice || isSuperAdmin;

  // Get current layout name from dashboardLayoutId
  const currentLayoutName = useMemo(() => {
    if (!shop?.layouts || !dashboardLayoutId) return undefined;

    const currentLayout = shop.layouts.find((layout) => {
      const layoutNames = Object.keys(layout);
      return layoutNames.length > 0 && layoutNames[0] === dashboardLayoutId;
    });

    return currentLayout ? Object.keys(currentLayout)[0] : undefined;
  }, [shop?.layouts, dashboardLayoutId]);

  // Available layout options for selection
  const layoutOptions = useMemo(() => {
    if (!layouts) return [];

    return layouts.map((layout) => {
      const layoutName = Object.keys(layout)[0];
      return {
        name: layoutName,
        displayName: layoutName.charAt(0).toUpperCase() + layoutName.slice(1),
        data: layout[layoutName],
      };
    });
  }, [layouts]);

  // Available containers based on selected layout
  const availableContainers = useMemo(() => {
    if (!state.selectedLayoutName || !layouts) return [];

    const selectedLayout = layouts.find(
      (layout) => Object.keys(layout)[0] === state.selectedLayoutName
    );

    if (!selectedLayout) return [];

    const layoutData = selectedLayout[state.selectedLayoutName];

    return layoutData
      .map((item) => {
        const container = shop?.containers?.find((c) => c.id === item.i);
        return {
          id: item.i,
          displayName: container?.displayName || item.i,
        };
      })
      .filter((container) => container.id !== card.currentContainer) // Exclude current container
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [
    state.selectedLayoutName,
    layouts,
    shop?.containers,
    card.currentContainer,
  ]);

  // Initialize with current layout when dialog opens
  useEffect(() => {
    if (isOpen && currentLayoutName && !state.selectedLayoutName) {
      setState((prev) => ({
        ...prev,
        selectedLayoutName: currentLayoutName,
        selectedContainerId: undefined,
        error: undefined,
      }));
    }
  }, [isOpen, currentLayoutName, state.selectedLayoutName]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setState({
        selectedLayoutName: undefined,
        selectedContainerId: undefined,
        isLoading: false,
        error: undefined,
      });
    }
  }, [isOpen]);

  const handleLayoutChange = (layoutName: string) => {
    setState((prev) => ({
      ...prev,
      selectedLayoutName: layoutName,
      selectedContainerId: undefined, // Reset container selection
      error: undefined,
    }));
  };

  const handleContainerChange = (containerId: string) => {
    setState((prev) => ({
      ...prev,
      selectedContainerId: containerId,
      error: undefined,
    }));
  };

  const handleMove = async () => {
    if (!state.selectedContainerId || !shop) {
      setState((prev) => ({
        ...prev,
        error: "Please select a container to move the repair order to",
      }));
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: undefined }));

    const toContainer = shop.containers.find(
      (c) => c.id === state.selectedContainerId
    );

    try {
      const labelChoice = await resolveBidirectionalLabelChoice(
        toContainer,
        shop.labelCatalog,
        card,
        chooseLabel
      );

      if (!labelChoice.proceed) {
        setState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      const technicianChoice = await resolveTechnicianChoice(
        toContainer,
        employees,
        card,
        chooseTechnician,
        isTechnicianWritebackEnabled && canAssignTechnician
      );

      if (!technicianChoice.proceed) {
        setState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      const moveResult = await httpsCallables.moveCard({
        cardId: card.id,
        shopId: shop.id,
        toContainerId: state.selectedContainerId,
        chosenBidirectionalLabel: labelChoice.chosenBidirectionalLabel,
        chosenTechnicianId: technicianChoice.chosenTechnicianId,
      });

      if (moveResult.data?.technicianSyncFailed) {
        toast({
          variant: "destructive",
          title: "Technician sync failed",
          description:
            "The card moved, but syncing the technician to Tekmetric failed.",
        });
      }

      // Call success callback if provided
      onMoveSuccess?.(card, state.selectedContainerId);

      // Close dialog
      onClose();
    } catch (error) {
      console.error("Failed to move repair order:", error);
      setState((prev) => ({
        ...prev,
        error:
          error instanceof Error
            ? error.message
            : `Failed to move ${terminolize("repair order", TerminologyOptions.repairOrder)}. Please try again.`,
        isLoading: false,
      }));
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const canMove = state.selectedContainerId && !state.isLoading;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon name={IconNames.move} size={18} />
              Move {terminolize(
                "Repair Order",
                TerminologyOptions.repairOrder
              )}{" "}
              to Container
            </DialogTitle>
            <DialogDescription>
              Select a layout and container to move "
              {card.primaryField || card.id}" to a different location.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Layout Selector */}
            <div className="grid gap-2">
              <label htmlFor="layout-select" className="text-sm font-medium">
                Layout
              </label>
              <LayoutSelector
                layouts={layoutOptions.map((c) => c.name)}
                selectedLayout={state.selectedLayoutName}
                onLayoutChange={handleLayoutChange}
                disabled={state.isLoading}
                className="w-full"
              />
            </div>

            {/* Container Selector */}
            <div className="grid gap-2">
              <label htmlFor="container-select" className="text-sm font-medium">
                Container
              </label>
              <Autocomplete
                options={availableContainers.map((c) => ({
                  value: c.id,
                  label: c.displayName,
                }))}
                value={state.selectedContainerId}
                onValueChange={handleContainerChange}
                placeholder={
                  !state.selectedLayoutName
                    ? "Select a layout first..."
                    : availableContainers.length === 0
                      ? "No available containers..."
                      : "Search or select a container..."
                }
                emptyText="No container found."
                disabled={state.isLoading || !state.selectedLayoutName}
                className="w-full"
              />
            </div>

            {/* Current container info */}
            <div className="text-sm text-muted-foreground">
              <strong>Current container:</strong>{" "}
              {shop?.containers?.find((c) => c.id === card.currentContainer)
                ?.displayName || card.currentContainer}
            </div>

            {/* Error message */}
            {state.error && (
              <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                <Icon name={IconNames.alertTriangle} size={16} />
                <span>{state.error}</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={state.isLoading}
            >
              Cancel
            </Button>
            <Button onClick={handleMove} disabled={!canMove}>
              {state.isLoading ? (
                <>
                  <Icon
                    name={IconNames.loader}
                    size={16}
                    className="mr-2 animate-spin"
                  />
                  Moving...
                </>
              ) : (
                <>
                  <Icon name={IconNames.move} size={16} className="mr-2" />
                  Move
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <LabelChoicePopup />
      <TechnicianChoicePopup />
    </>
  );
};
